import { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Generic hook for API calls
function useApi<T>(
  fetchFn: () => Promise<api.ApiResponse<T>>,
  deps: unknown[] = []
): UseApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchFn();
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.error || 'Unknown error');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  }, deps);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// Specific hooks for each API endpoint
export const useHealth = () => useApi(() => api.getHealth());
export const useStatus = () => useApi(() => api.getStatus());
export const useIdentity = () => useApi(() => api.getIdentity());
export const useSkills = () => useApi(() => api.listSkills());
export const usePersonas = () => useApi(() => api.listPersonas());
export const useTokenUsage = () => useApi(() => api.getTokenUsage());
export const useCausalGraph = () => useApi(() => api.getCausalGraph());
export const useAgentRankings = () => useApi(() => api.getAgentRankings());
export const useKGStats = () => useApi(() => api.getKGStats());
export const useAgentStatuses = () => useApi(() => api.getAgentStatuses());

// Hook for executing skills
export function useSkillExecution() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<api.ApiResponse<{
    success: boolean;
    data?: Record<string, unknown>;
    error?: string;
    metadata: { executionTimeMs: number; identityAligned: boolean; tokensUsed?: number };
  }> | null>(null);

  const execute = useCallback(async (
    name: string,
    agentRole: string,
    payload: Record<string, unknown>,
    context?: Record<string, unknown>
  ) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.executeSkill(name, agentRole, payload, context);
      setResult(response);
      return response;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Execution failed';
      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, result };
}

// Hook for orchestration
export function useOrchestration() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<api.ApiResponse<{
    taskId: string;
    results: Array<{
      success: boolean;
      data?: Record<string, unknown>;
      error?: string;
      metadata: { executionTimeMs: number; identityAligned: boolean };
    }>;
    conflicts: Array<{
      id: string;
      agents: string[];
      type: string;
      description: string;
    }>;
    budgetReport: {
      consumedTokens: Record<string, number>;
      violations: string[];
      utilizationPercent: Record<string, number>;
    };
    checkpointViolations: number;
    totalExecutionTimeMs: number;
  }> | null>(null);

  const orchestrate = useCallback(async (requests: Array<{
    skillName: string;
    input: { skillName: string; context: Record<string, unknown>; payload: Record<string, unknown> };
    preferredAgent?: string;
  }>) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.orchestrate(requests);
      setResult(response);
      return response;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Orchestration failed';
      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { orchestrate, loading, error, result };
}

// Hook for DSS queries
export function useDSS() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<api.ApiResponse<{
    answer: string;
    causalScore: number;
    confidenceInterval: [number, number];
    contributingAgents: string[];
    reasoning: string;
  }> | null>(null);

  const query = useCallback(async (
    queryText: string,
    mode: 'sales_rep' | 'manager' | 'customer' | 'admin' = 'sales_rep',
    context?: Record<string, unknown>
  ) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await api.queryDSS(queryText, mode, context);
      setResponse(result);
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'DSS query failed';
      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { query, loading, error, response };
}

// Hook for WebSocket connection
export function useWebSocket() {
  const [connected, setConnected] = useState(false);
  const [logs, setLogs] = useState<Array<{
    time: string;
    agent: string;
    action: string;
    status: string;
  }>>([]);
  const [agentStatuses, setAgentStatuses] = useState<Record<string, {
    status: string;
    tokensUsed: number;
  }>>({});

  useEffect(() => {
    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:3001/ws';
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setConnected(true);
      console.log('[WS] Connected');
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'log') {
          setLogs((prev) => [message.payload, ...prev].slice(0, 50));
        } else if (message.type === 'agent_status') {
          setAgentStatuses((prev) => ({
            ...prev,
            [message.payload.agent]: {
              status: message.payload.status,
              tokensUsed: message.payload.tokensUsed,
            },
          }));
        }
      } catch (error) {
        console.error('[WS] Message parse error:', error);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      console.log('[WS] Disconnected');
    };

    ws.onerror = (error) => {
      console.error('[WS] Error:', error);
    };

    return () => {
      ws.close();
    };
  }, []);

  return { connected, logs, agentStatuses };
}
